import { Box } from '@chakra-ui/react';
import { ReactElement, ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { MultiSelectList } from './MultiSelectList';
import { MultiSelectSearchInput } from './MultiSelectSearchInput';

const DEFAULT_PORTAL_TARGET = typeof document !== 'undefined' ? document.body : null;

type DropdownStyle = {
  top: number;
  left: number;
  width: number;
};

type NormalizedOption = {
  value: string;
  label: string;
  secondaryText?: string;
};

type MultiSelectDropdownProps = {
  isOpen: boolean;
  dropdownStyle: DropdownStyle | null;
  maxHeight: number;
  isReady?: boolean;
  /**
   * When set, the menu is portaled here (e.g. multiselect container inside a modal) so focus traps
   * on `Dialog.Content` still include the dropdown. Defaults to `document.body`.
   */
  portalContainerRef?: RefObject<HTMLElement | null>;
  dropdownRef: RefObject<HTMLDivElement>;
  searchable: boolean;
  searchQuery: string;
  searchPlaceholder: string;
  searchInputRef: RefObject<HTMLInputElement>;
  onSearchChange: (value: string) => void;
  onClose: () => void;
  options: NormalizedOption[];
  selectedValues: string[];
  onToggle: (value: string) => void;
  emptyMessage?: ReactNode;
  /** Shown under the search field when searchable */
  searchHint?: ReactNode;
};

type MultiSelectDropdownPanelProps = Omit<MultiSelectDropdownProps, 'portalContainerRef'>;

/**
 * MultiSelectDropdown component - Portal-rendered dropdown container
 * Escapes modal overflow boundaries
 */
const MultiSelectDropdownComponent = ({
  isOpen,
  dropdownStyle,
  maxHeight,
  isReady = true,
  dropdownRef,
  searchable,
  searchQuery,
  searchPlaceholder,
  searchInputRef,
  onSearchChange,
  onClose,
  options,
  selectedValues,
  onToggle,
  emptyMessage,
  searchHint,
}: MultiSelectDropdownPanelProps): ReactElement | null => {
  if (!isOpen || !dropdownStyle) {
    return null;
  }

  return (
    <Box
      ref={dropdownRef}
      position="fixed"
      top={`${dropdownStyle.top}px`}
      left={`${dropdownStyle.left}px`}
      width={`${dropdownStyle.width}px`}
      bg="background.primary"
      borderRadius="l"
      boxShadow="0px 0px 16px 0px rgba(0,0,0,0.1)"
      zIndex="10000"
      maxH={`${maxHeight}px`}
      // Set to a fixed height to avoid jumping when the Amount of list Items changes.
      // If we want to make this dynamic we would need to also invert layout with search if it stays on top.
      height="350px"
      overflow="hidden"
      border="default"
      borderColor="border.accent"
      display="flex"
      flexDirection="column"
      /**
       * Do not use `visibility:hidden` + `pointer-events:none` while positioning: clicks fall through
       * to the trigger (toggle) or miss the panel; dialog focus trap + outside-click also break.
       */
      opacity={isReady ? 1 : 0}
      pointerEvents="auto"
      aria-hidden={!isReady}
    >
      {/* Search Input - Sticky at top */}
      {searchable && (
        <MultiSelectSearchInput
          searchQuery={searchQuery}
          searchPlaceholder={searchPlaceholder}
          searchInputRef={searchInputRef}
          onSearchChange={onSearchChange}
          onClose={onClose}
          hint={searchHint}
        />
      )}
      {/* Scrollable content area */}
      <Box
        overflowY="auto"
        overflowX="hidden"
        flex="1"
        css={{
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#E5E5E5',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#CCCCCC',
          },
        }}
      >
        <MultiSelectList
          options={options}
          selectedValues={selectedValues}
          onToggle={onToggle}
          emptyMessage={emptyMessage}
        />
      </Box>
    </Box>
  );
};

export const MultiSelectDropdown = ({
  portalContainerRef,
  ...props
}: MultiSelectDropdownProps): ReactElement | null => {
  const root = portalContainerRef?.current ?? DEFAULT_PORTAL_TARGET;
  if (!root) {
    return null;
  }
  return createPortal(<MultiSelectDropdownComponent {...props} />, root);
};
